import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, CalendarDays, FolderOpen, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useArchivePlanningWeek,
  useDeletePlanningWeek,
  useGeneratePlanningWeek,
  usePlanningWeeks,
  useUnarchivePlanningWeek,
} from '@/hooks/use-planning';
import type { PlanningWeek } from '@/features/planning/types';
import { formatDisplayDateRange } from '@/lib/date-format';

function weekRangeLabel(week: PlanningWeek): string {
  return formatDisplayDateRange(week.startDate, week.endDate);
}

function LiveStatusDot({ isLive }: { isLive: boolean }) {
  return (
    <span
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
        isLive ? 'bg-emerald-500' : 'bg-red-500'
      }`}
      title={isLive ? 'Live' : 'Draft'}
      aria-label={isLive ? 'Live' : 'Draft'}
    />
  );
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultPlanningStartDate(): string {
  const today = new Date();
  const day = today.getDay();
  const daysUntilMonday = day === 1 ? 0 : (8 - day) % 7;
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + daysUntilMonday);
  return toDateInputValue(nextMonday);
}

function isMondayDateString(value: string): boolean {
  if (!value) return false;
  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime()) && parsed.getDay() === 1;
}

export function PlanningPage() {
  const navigate = useNavigate();
  const { hasCapability } = usePermissions();
  const [showArchived, setShowArchived] = useState(false);
  const { data: weeks = [], isLoading, isError } = usePlanningWeeks({
    includeArchived: showArchived,
  });
  const generateWeek = useGeneratePlanningWeek();
  const archiveWeek = useArchivePlanningWeek();
  const unarchiveWeek = useUnarchivePlanningWeek();
  const deleteWeek = useDeletePlanningWeek();
  const canGenerateWeek = hasCapability('planning.week.generate');
  const canArchiveWeek = hasCapability('planning.week.archive');
  const canDeleteWeek = hasCapability('planning.week.delete');
  const [startDate, setStartDate] = useState(getDefaultPlanningStartDate);
  const [formError, setFormError] = useState<string | null>(null);
  const [successNote, setSuccessNote] = useState<string | null>(null);
  const activeWeeks = useMemo(() => weeks.filter((week) => !week.isArchived), [weeks]);
  const archivedWeeks = useMemo(() => weeks.filter((week) => week.isArchived), [weeks]);
  const hasWeekWorklogs = (week: PlanningWeek): boolean =>
    week.hasWorklogs ?? (week.worklogCount ?? 0) > 0;

  const handleArchiveWeek = async (week: PlanningWeek) => {
    const confirmed = window.confirm(
      `Archive week ${weekRangeLabel(week)}?\n\nArchived weeks are hidden from the active list but can be restored later.`,
    );
    if (!confirmed) return;
    try {
      await archiveWeek.mutateAsync(week.id);
      toast({ title: 'Planning week archived' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to archive planning week',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleUnarchiveWeek = async (week: PlanningWeek) => {
    const confirmed = window.confirm(
      `Restore week ${weekRangeLabel(week)} to active planning?`,
    );
    if (!confirmed) return;
    try {
      await unarchiveWeek.mutateAsync(week.id);
      toast({ title: 'Planning week restored' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to restore planning week',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteWeek = async (week: PlanningWeek) => {
    const confirmed = window.confirm(
      `Permanently delete week ${weekRangeLabel(week)}?\n\nThis cannot be undone. Weeks with worklogs cannot be deleted and must be archived instead.`,
    );
    if (!confirmed) return;

    try {
      await deleteWeek.mutateAsync(week.id);
      toast({ title: 'Planning week deleted' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to delete planning week',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleGenerateWeek = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setSuccessNote(null);

    if (!startDate) {
      setFormError('Start date is required.');
      return;
    }

    if (!isMondayDateString(startDate)) {
      setFormError('Start date must be a Monday.');
      return;
    }

    try {
      const generatedWeek = await generateWeek.mutateAsync(startDate);
      const generatedWeekId = generatedWeek?.id;

      if (generatedWeekId) {
        toast({ title: 'Planning week generated' });
        navigate(`/planning/weeks/${generatedWeekId}`);
        return;
      }

      setSuccessNote('Planning week generated. The week list has been refreshed.');
      toast({ title: 'Planning week generated' });
    } catch (error: unknown) {
      setFormError(normalizeError(error).message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Planning</h1>
          <p className="text-muted-foreground">Review weeks and open a specific planning week.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="show-archived-weeks" className="text-sm text-muted-foreground">
            Show archived
          </Label>
          <Switch
            id="show-archived-weeks"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
        </div>
      </div>

      {canGenerateWeek && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generate Week</CardTitle>
            <CardDescription>
              Create a planning week by selecting a Monday start date.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerateWeek} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="planning-start-date">Week start date</Label>
                <Input
                  id="planning-start-date"
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">Start date must be a Monday.</p>
              </div>

              {formError && <p className="text-sm text-destructive">{formError}</p>}
              {successNote && <p className="text-sm text-foreground">{successNote}</p>}

              <Button type="submit" className="gap-2" disabled={generateWeek.isPending}>
                <Plus className="h-4 w-4" />
                {generateWeek.isPending ? 'Generating week...' : 'Generate Week'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">Loading planning weeks...</CardContent>
        </Card>
      )}

      {isError && (
        <Card>
          <CardContent className="py-8 text-sm text-destructive">
            Failed to load planning weeks.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && activeWeeks.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>No active planning weeks</CardTitle>
            <CardDescription>
              No planning weeks exist yet. Generate the first planning week to get started.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && !isError && activeWeeks.length > 0 && (
        <div className="grid gap-3">
          {activeWeeks.map((week) => {
            const weekHasWorklogs = hasWeekWorklogs(week);
            return (
              <Card key={week.id} className="transition-colors hover:border-primary/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <LiveStatusDot isLive={week.isLive} />
                    {weekRangeLabel(week)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{week._count?.items ?? week.items?.length ?? 0} items</span>
                    <span>{weekHasWorklogs ? `${week.worklogCount ?? 0} worklog(s)` : 'No worklogs'}</span>
                  </div>

                  {week.note && (
                    <p className="line-clamp-2 rounded border border-border/70 bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                      {week.note}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => navigate(`/planning/weeks/${week.id}`)}
                    >
                      <CalendarDays className="h-4 w-4" />
                      Open week
                    </Button>
                    <div className="flex flex-wrap gap-2">
                      {canArchiveWeek && (
                        <Button
                          variant="secondary"
                          className="gap-2"
                          disabled={archiveWeek.isPending}
                          onClick={() => void handleArchiveWeek(week)}
                        >
                          <Archive className="h-4 w-4" />
                          Archive
                        </Button>
                      )}
                      {canDeleteWeek && (
                        <Button
                          variant="destructive"
                          className="gap-2"
                          disabled={deleteWeek.isPending || weekHasWorklogs}
                          onClick={() => void handleDeleteWeek(week)}
                          title={
                            weekHasWorklogs
                              ? 'This week has worklogs and cannot be deleted. Archive it instead.'
                              : 'Delete permanently'
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete permanently
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!isLoading && !isError && showArchived && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Archived weeks</CardTitle>
            <CardDescription>Archived weeks are hidden from active planning by default.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {archivedWeeks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No archived weeks found.</p>
            ) : (
              archivedWeeks.map((week) => {
                const weekHasWorklogs = hasWeekWorklogs(week);
                return (
                  <div key={week.id} className="rounded-md border border-border/70 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          <LiveStatusDot isLive={week.isLive} />
                          {weekRangeLabel(week)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {week._count?.items ?? week.items?.length ?? 0} items
                          {weekHasWorklogs ? ` · ${week.worklogCount ?? 0} worklog(s)` : ' · No worklogs'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={() => navigate(`/planning/weeks/${week.id}`)}
                        >
                          <FolderOpen className="h-4 w-4" />
                          View
                        </Button>
                        {canArchiveWeek && (
                          <Button
                            variant="secondary"
                            className="gap-2"
                            disabled={unarchiveWeek.isPending}
                            onClick={() => void handleUnarchiveWeek(week)}
                          >
                            <RotateCcw className="h-4 w-4" />
                            Unarchive
                          </Button>
                        )}
                        {canDeleteWeek && (
                          <Button
                            variant="destructive"
                            className="gap-2"
                            disabled={deleteWeek.isPending || weekHasWorklogs}
                            onClick={() => void handleDeleteWeek(week)}
                            title={
                              weekHasWorklogs
                                ? 'This week has worklogs and cannot be deleted. Archive it instead.'
                                : 'Delete permanently'
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete permanently
                          </Button>
                        )}
                      </div>
                    </div>
                    {week.note && (
                      <p className="mt-2 line-clamp-2 rounded border border-border/70 bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                        {week.note}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
